import { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import {
  selectUserInfo,
  selectUserToken,
} from '../../../../../redux/features/user/userSelectors';
import { useCreateMailingPreviewMutation } from '../../../../../redux/services/emails/emailsApi';
import { useEmailContext } from '../../../../../contexts/EmailContext';

import { InputsTemplate } from '../../../inputs/InputsTemplate';
import { EmailDocumentsList } from '../EmailDocumentsList';
import { DocumentInput } from '../../../inputs/documents/DocumentInput';
import { EmailRealtyPreview } from '../EmailRealtyPreview';
import { RealtyPickerInput } from '../RealtyPickerInput';
import { EmailPreview } from '../../EmailPreview';
import { Dialog } from '../../../../ui/Dialog';
import { ToggleGroupTemplate } from '../../../inputs/ToggleGroupTemplate';

import { showError } from '../../../../../utils/ui';
import { toast } from 'sonner';
import { errorMessages, variablesValues } from '../../../../../data/constants';
import { addVariablesInText } from '../../../../../utils/utils';
import { langRadioData } from '../../../../../lib/mocks/add-client-mock';
import {
  emailFieldNames,
  localeField,
  textareaValues,
} from '../../../../../lib/mocks/email-mock';

export const EmailFormInputs = ({
  formProps = {},
  client = null,
  inputs = [],
  inputsTextareas = [],
  setInputsTextareas = () => {},
  clientId = null,
  setClientId = () => {},
  clientData = null,
  isClientDataLoading = false,
  clientDataError = null,
}) => {
  const [isPreviewShown, setIsPreviewShown] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const fileChangeRef = useRef(null);

  const userToken = useSelector(selectUserToken);
  const userInfo = useSelector(selectUserInfo);

  const { selectedRealties, setRequestId } = useEmailContext();

  const [createMailingPreview] = useCreateMailingPreviewMutation();

  const setFieldValue = (inputName, value) => {
    formProps?.setFieldValue(inputName, value);
  };

  const handleLocaleChange = () => {
    const localeValue = formProps?.values?.[emailFieldNames.locale];

    if (localeValue || localeValue === 0) {
      setInputsTextareas(prevInpunts =>
        prevInpunts.map(input => ({
          ...input,
          defaultValue: textareaValues[emailFieldNames[input.name]][Number(localeValue)],
        }))
      );
    }
  };

  const applyClientLocale = clientLocale => {
    if (clientLocale == null || clientLocale === 1) return;

    const matched = langRadioData.find(
      lang => Number(lang.value) === Number(clientLocale)
    );

    if (matched) {
      setFieldValue(emailFieldNames.locale, matched.value);
    }
  };

  const handleClearClient = () => {
    formProps?.setFieldValue(emailFieldNames.client_id, '', false);
    setClientId(null);
    setTimeout(() => formProps?.validateForm(), 0);
  };

  const handleClientSelect = (client, request = null) => {
    const name = [client?.first_name, client?.last_name]
      .filter(Boolean)
      .join(' ');
    const displayName = name || `ID: ${client.id}`;

    setFieldValue(emailFieldNames.client_id, displayName);
    setClientId(client.id);
    applyClientLocale(client?.locale?.value);

    if (request?.id) {
      setRequestId(request.id);
    }
  };

  const handleClientData = () => {
    if (clientDataError) {
      showError(errorMessages.wentWrong);
      return;
    }

    if (clientData && !isClientDataLoading && clientId && !client) {
      const name = [clientData?.first_name, clientData?.last_name]
        .filter(Boolean)
        .join(' ');

      const displayName = name || `ID: ${clientId}`;
      setFieldValue(emailFieldNames.client_id, displayName);
    }
  };

  const handlePreviewClick = async () => {
    const subjectValue = formProps?.values?.[emailFieldNames.subject];
    const titleValue = formProps?.values?.[emailFieldNames.title];
    const textValue = formProps?.values?.[emailFieldNames.text];
    if (!subjectValue || !titleValue || !textValue) {
      showError(errorMessages.fillEmailInputs);
    } else {
      const values = {
        [emailFieldNames.subject]: subjectValue,
        [emailFieldNames.text]: textValue,
        [emailFieldNames.title]: titleValue,
      };

      const clientInfo = client ? client : clientData;
      const newValues = await addVariablesInText(
        values,
        clientInfo,
        userInfo,
        variablesValues
      );

      const response = await createMailingPreview({
        token: userToken,
        data: JSON.stringify({
          client_id: client?.id ?? clientId,
          subject: newValues[emailFieldNames.subject],
          title: newValues[emailFieldNames.title],
          text: newValues[emailFieldNames.text],
          realty_ids: selectedRealties?.map(r => r.id) ?? [],
        }),
      });

      if (response?.error || response?.errors) {
        showError(errorMessages.wentWrong);
      } else {
        setHtmlContent(response?.data);
        setIsPreviewShown(true);
      }
    }
  };

  const handleClientInfo = () => {
    if (client) {
      const name = [client?.first_name, client?.last_name]
        .filter(Boolean)
        .join(' ');
      const displayName = name || `ID: ${client.id}`;
      formProps?.setFieldValue(emailFieldNames.client_id, displayName);
      applyClientLocale(client?.locale?.value);
    }
  };

  useEffect(() => {
    handleLocaleChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProps?.values?.[emailFieldNames.locale]]);

  useEffect(() => {
    handleClientData();
  }, [clientData, isClientDataLoading, clientDataError]);

  useEffect(() => {
    handleClientInfo();
  }, [client]);

  const localeValue = useMemo(() => {
    const raw = formProps?.values?.[emailFieldNames.locale];
    return (
      langRadioData.find(lang => Number(lang.value) === Number(raw)) ??
      langRadioData.find(lang => Number(lang.value) === 1)
    );
  }, [formProps?.values]);

  const inputsWithSelect = useMemo(
    () =>
      inputs.map(input =>
        input.name === emailFieldNames.client_id
          ? { ...input, onClientSelect: handleClientSelect, onClearClient: handleClearClient }
          : input
      ),
    [inputs]
  );

  const isPreviewDisabled = !formProps?.values?.[emailFieldNames.client_id];

  const isSendDisabled =
    !formProps.dirty ||
    Object.keys(formProps.errors).length > 0 ||
    formProps.isSubmitting;

  const btnBase = 'px-4 py-2 rounded text-sm font-medium border transition-colors';
  const btnActive = 'bg-purpleColor border-purpleColor text-white hover:opacity-90 cursor-pointer';
  const btnInactive = 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed';

  return (
    <>
      <ToggleGroupTemplate
        formProps={formProps}
        defaultValue={localeValue}
        controlledValue={localeValue.value}
        {...localeField}
      />

      <InputsTemplate
        formProps={formProps}
        inputsList={inputsWithSelect}
      />

      <InputsTemplate
        formProps={formProps}
        inputsList={inputsTextareas}
      />

      <EmailRealtyPreview />

      <RealtyPickerInput />

      <EmailDocumentsList formProps={formProps} onFileChange={fileChangeRef} />

      <div className="flex justify-between items-center w-full lg:max-w-[768px]">
        <DocumentInput handleChange={e => fileChangeRef.current?.(e)} />
        <div className="flex gap-2">
          <button
            type="button"
            disabled={isPreviewDisabled}
            onClick={isPreviewDisabled ? undefined : handlePreviewClick}
            className={`${btnBase} ${isPreviewDisabled ? btnInactive : btnActive}`}
          >
            Show Preview
          </button>
          <button
            type="submit"
            disabled={isSendDisabled}
            className={`${btnBase} ${isSendDisabled ? btnInactive : btnActive}`}
          >
            Send
          </button>
        </div>
      </div>

      {isPreviewShown && (
        <Dialog
          content={<EmailPreview htmlContent={htmlContent} />}
          classes={'w-fit'}
          isOpen={isPreviewShown}
          onClose={() => setIsPreviewShown(false)}
        />
      )}
    </>
  );
};
